import { useTranslation } from "react-i18next";
import type { Clinic } from "@fysaro/domain";
import { useCreateBookingSession } from "./hooks/useCreateBookingSession";
import { formatMoney } from "@fysaro/shared";
import { getInitials } from "@/shared/lib/get-initials";
import { Loader2 } from "lucide-react";
import { redirectToApp } from "@/utils/appUrl";

export function PublicClinicServices({ clinic }: { clinic: Clinic }) {
  const { t, i18n } = useTranslation(["clinic", "booking-patient"]);
  const createSession = useCreateBookingSession();

  // Validamos si la clí­nica viene mapeada correctamente con sus servicios
  const servicesList = clinic.services || [];

  const handleSelectService = async (serviceId: any) => {
    try {
      // Generamos la sesión puente en la BD de forma así­ncrona
      const response = await createSession.mutateAsync({
        clinicId: clinic.id,
        serviceId,
        source: "DIRECT_LINK", // ðŸŽ¯ Indicamos que es un enlace directo sin servicio
      });

      // Viajamos limpios usando el sessionId recién creado
      const targetPath = `/patient/bookings/landing?sessionId=${response.id}`;
      redirectToApp(targetPath);
    } catch (error) {
      console.error("Error al crear la sesión puente de reserva", error);
    }
  };

  return (
    <div className="space-y-6 text-left">
      <div className="space-y-1">
        <h2 className="text-xl font-black tracking-tight text-foreground">
          {t("clinic:tabs.services", "Nuestros Servicios")}
        </h2>
        <p className="text-xs md:text-sm text-muted-foreground">
          {t("clinic:tabs.services_desc")}
        </p>
      </div>

      {servicesList.length > 0 ? (
        <div className="bg-card/40 rounded-2xl border border-border/40 p-3 md:p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {servicesList.map(
            (service: {
              id: string;
              name: string;
              price: number;
              color?: string | null;
              icon?: string | null;
              description?: string | null;
              duration?: number | null;
              defaultDuration?: number | null;
            }) => (
              <button
                key={service.id}
                type="button"
                onClick={() => handleSelectService(service.id)}
                disabled={createSession.isPending}
                className="w-full p-5 rounded-2xl bg-card border border-border/60 shadow-sm flex items-center justify-between gap-4 hover:border-primary/40 hover:bg-primary/5 transition-all text-left group active:scale-[0.99]"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <span
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shadow-inner shrink-0"
                    style={{
                      backgroundColor: `${service.color ?? "#ccc"}15`,
                      color: service.color || "var(--primary)",
                    }}
                  >
                    {getInitials(service.name, "SR")}
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-bold text-foreground group-hover:text-primary transition-colors text-sm md:text-base truncate">
                      {service.name}
                    </h3>
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                      {service.description ||
                        t(
                          "clinic:tabs.services.no_desc",
                          "Sin descripción disponible.",
                        )}
                    </p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {createSession.isPending ? (
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  ) : (
                  <span className="text-sm md:text-base font-black text-foreground">
                    {formatMoney(
                      service.price,
                      clinic.businessConf?.currency ?? "EUR",
                      i18n.language || clinic.businessConf?.language || "es",
                    )}
                  </span>
                  )}
                </div>
              </button>
            ),
          )}
        </div>
      ) : (
        <div className="text-center p-12 bg-muted/20 border border-dashed rounded-2xl text-xs text-muted-foreground font-medium">
          {t("clinic:tabs.services_empty")}
        </div>
      )}
    </div>
  );
}
